import { Injectable } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { SigningKeyService } from './signing-key.service';

const EMPTY_BODY_SHA256_BASE64URL = '47DEQpj8HBSa-_TImW-5JCeuQeRkm5NMpJWZG3hSuFU';
const SIGNATURE_ALG = 'ECDSA_P256_SHA256_P1363';

export interface SignatureHeaders {
  'X-Key-Id': string;
  'X-Timestamp': string;
  'X-Nonce': string;
  'X-Body-SHA256': string;
  'X-Signature-Alg': string;
  'X-Signature': string;
}

@Injectable({ providedIn: 'root' })
export class RequestSigningService {
  constructor(private keyService: SigningKeyService) { }

  async sign(request: HttpRequest<any>, token: string): Promise<SignatureHeaders | null> {
    const privateKey = this.keyService.getPrivateKey();
    const keyId = this.keyService.getKeyId();
    if (!privateKey || !keyId) {
      return null;
    }

    const timestamp = Date.now().toString();
    const nonce = generateNonce();
    const canonicalUrl = canonicalizeUrl(request.urlWithParams);
    const bodyHash = await sha256Base64Url(serializeBody(request.body));
    const tokenHash = await sha256Base64Url(token || '');

    const canonical = [
      request.method.toUpperCase(),
      canonicalUrl,
      timestamp,
      nonce,
      bodyHash,
      tokenHash
    ].join('\n');

    const signatureBytes = await window.crypto.subtle.sign(
      { name: 'ECDSA', hash: { name: 'SHA-256' } },
      privateKey,
      new TextEncoder().encode(canonical)
    ) as ArrayBuffer;

    return {
      'X-Key-Id': keyId,
      'X-Timestamp': timestamp,
      'X-Nonce': nonce,
      'X-Body-SHA256': bodyHash,
      'X-Signature-Alg': SIGNATURE_ALG,
      'X-Signature': arrayBufferToBase64Url(signatureBytes)
    };
  }
}

function serializeBody(body: any): string {
  if (body === null || body === undefined) {
    return '';
  }
  if (typeof body === 'string') {
    return body;
  }
  return JSON.stringify(body);
}

async function sha256Base64Url(input: string): Promise<string> {
  if (input === '') {
    return EMPTY_BODY_SHA256_BASE64URL;
  }
  const bytes = new TextEncoder().encode(input);
  const hash = await window.crypto.subtle.digest('SHA-256', bytes) as ArrayBuffer;
  return arrayBufferToBase64Url(hash);
}

function canonicalizeUrl(fullUrl: string): string {
  const queryStart = fullUrl.indexOf('?');
  if (queryStart < 0) {
    return fullUrl;
  }
  const base = fullUrl.substring(0, queryStart);
  const query = fullUrl.substring(queryStart + 1);
  if (!query) {
    return base;
  }
  const sorted = query
    .split('&')
    .filter(pair => pair.length > 0)
    .sort((a, b) => {
      const keyA = keyOf(a);
      const keyB = keyOf(b);
      if (keyA < keyB) { return -1; }
      if (keyA > keyB) { return 1; }
      return a < b ? -1 : (a > b ? 1 : 0);
    })
    .join('&');
  return base + '?' + sorted;
}

function keyOf(pair: string): string {
  const eq = pair.indexOf('=');
  return eq < 0 ? pair : pair.substring(0, eq);
}

function generateNonce(): string {
  const c = window.crypto as any;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < bytes.length; i++) {
    hex.push(('00' + bytes[i].toString(16)).slice(-2));
  }
  return (
    hex.slice(0, 4).join('') + '-' +
    hex.slice(4, 6).join('') + '-' +
    hex.slice(6, 8).join('') + '-' +
    hex.slice(8, 10).join('') + '-' +
    hex.slice(10, 16).join('')
  );
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
